import { Instance } from 'mobx-state-tree';
import * as React from 'react';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { TLCode } from '../../common/enums';
import { inject, observer } from 'mobx-react';
import styled from '@emotion/styled';
import { IconNames } from '@blueprintjs/icons';
import { Button, Intent, Tooltip, Label } from '@blueprintjs/core';
import { Position } from '@blueprintjs/core/lib/esm/common/position';
import { BankHoliday } from '../../store/models/BankHoliday';
import { BankHolidayDate } from '../../store/models/BankHolidayDate';
import BankHolidayDateForm from './BankHolidayDateForm';
import moment from 'moment';
import { ConfirmationDialog } from '../common/dialog/ConfirmationDialog';
import { FormType } from '../../common/enums/FormType';
import { DataGrid, Column, Button as DGButton } from 'devextreme-react/data-grid';
import DataSource from 'devextreme/data/data_source';
import { AuthenticationStore } from '../../store';

const ButtonsContainer = styled.div({
  display: 'flex',
  alignContent: 'center',
  justifyContent: 'flex-end',
  marginBottom: 5,
});

interface BankHolidayDateManagerProps extends WithTranslateProps {
  onBankHolidayDateSave: (bhd: Instance<typeof BankHolidayDate>) => void;
  onBankHolidayDateDelete: (bhd: Instance<typeof BankHolidayDate>) => void;
  bankHolidayList: Array<Instance<typeof BankHoliday>>;
  bankHolidayDateList: Array<Instance<typeof BankHolidayDate>>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
}

interface BankHolidayDateManagerState {
  bankHolidayDateEdition?: Instance<typeof BankHolidayDate>;
  confirmDeleteOpen: boolean;
  selectedItemIdToDelete?: number;
  selectedYear: number;
  canEdit: boolean;
}

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ContainerSearchForm = styled.div`
  display: flex;
  flex: 0;
  flex-direction: row;
  .bp3-form-content {
    margin-right: 5px;
  },
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  height: 600px;
  justify-content: space-between;
`;

@inject('AuthenticationStore')
@observer
class BankHolidayDateManager extends React.Component<BankHolidayDateManagerProps, BankHolidayDateManagerState> {
  state = {
    bankHolidayDates: [],
    bankHolidayDateEdition: undefined,
    confirmDeleteOpen: false,
    selectedItemIdToDelete: undefined,
    selectedYear: moment().year(),
    canEdit: this.props.AuthenticationStore ? (this.props.AuthenticationStore.isAdmin || this.props.AuthenticationStore.isSuperAdmin) : false
  } as BankHolidayDateManagerState;

  buildGridData = () => {
    const data = this.props.bankHolidayDateList
      .filter((bhd) => moment(bhd.date).year() === this.state.selectedYear)
      .map((bhd) => ({
        ...bhd,
        id: bhd.bankHolidayDateId,
        bkname: bhd.bankHoliday !== undefined && bhd.bankHoliday !== null ? bhd.bankHoliday.name : '',
        bkcode: bhd.bankHoliday !== undefined && bhd.bankHoliday !== null ? bhd.bankHoliday.code : '',
      }));
    return new DataSource({
      store: { type: 'array', key: 'id', data: data },
      langParams: {
        locale: 'fr',
        collatorOptions: {
          sensitivity: 'base',
        },
      },
    });
  };

  stopEdition = () => {
    this.setState({ bankHolidayDateEdition: undefined });
  };

  save = (data: any) => {
    data.date.setTime(data.date.getTime() + 4 * 60 * 60 * 1000); // pour éviter les problèmes de time zone
    this.props.onBankHolidayDateSave(data);
    this.stopEdition();
  };

  deleteBankHolidaySelected = () => {
    const bh = this.props.bankHolidayDateList.find(
      (item) => item.bankHolidayDateId === this.state.selectedItemIdToDelete,
    );
    if (bh !== undefined) {
      this.props.onBankHolidayDateDelete(bh);
    }
    this.setState({ confirmDeleteOpen: false, selectedItemIdToDelete: undefined });
  };

  getBankHolidayDateToDeleteDetails = () => {
    const bh = this.props.bankHolidayDateList.find(
      (item) => item.bankHolidayDateId === this.state.selectedItemIdToDelete,
    );
    if (bh === undefined) return {};
    return { bankHolidayCode: bh.bankHoliday!.code, bankHolidayDate: moment(bh.date).format('ddd DD/MM/YYYY') };
  };

  render() {
    const { bankHolidayDateEdition } = this.state;
    const { translate, bankHolidayList } = this.props;
    return (
      <Container>
        <ButtonsContainer>
          {this.state.canEdit && (<Button
            text={translate(TLCode.Add)}
            intent={Intent.PRIMARY}
            disabled={!this.state.canEdit}
            onClick={() => {
              this.setState({
                bankHolidayDateEdition: BankHolidayDate.create({
                  bankHolidayDateId: 0,
                  bankHolidayId: 0,
                  date: moment().format('DD/MM/YYYY'),
                }),
              });
            }}
          />)}
          &nbsp;&nbsp;&nbsp;&nbsp;
          <a href="http://www.public-holidays.us/BE_FR_2020_All" target="_blank" rel="noopener noreferrer">
            Site de référence
          </a>
        </ButtonsContainer>
        <BankHolidayDateForm
          bankHolidayDate={bankHolidayDateEdition}
          bankHolidayList={bankHolidayList}
          onSave={this.save}
          onStop={this.stopEdition}
          formType={
            bankHolidayDateEdition &&
            (bankHolidayDateEdition.bankHolidayDateId !== -1 && bankHolidayDateEdition.bankHolidayDateId !== 0)
              ? FormType.UPDATE
              : FormType.CREATE
          }
        />
        <ConfirmationDialog
          isOpen={this.state.confirmDeleteOpen}
          onContinue={this.deleteBankHolidaySelected}
          title={this.props.translate(TLCode.SECRETARIAT_DASHBOARD_BH_Delete_Confirm_Title)}
          messageContent={
            <>
              {this.props.translate(TLCode.SECRETARIAT_DASHBOARD_BH_Delete_Confirm_Text, {
                bankHolidayCode: this.getBankHolidayDateToDeleteDetails().bankHolidayCode,
                bankHolidayDate: this.getBankHolidayDateToDeleteDetails().bankHolidayDate,
              })}
            </>
          }
          confirmText={this.props.translate(TLCode.GENERAL_Delete)}
          cancelText={this.props.translate(TLCode.GENERAL_Cancel)}
          onCancel={() => this.setState({ confirmDeleteOpen: false, selectedItemIdToDelete: undefined })}
        />
        <ContainerSearchForm>
          <Tooltip
            content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_PreviousYear)}
            position={Position.BOTTOM}
            hoverOpenDelay={750}
          >
            <Button
              icon={IconNames.CHEVRON_LEFT}
              large={true}
              style={{ height: '30px', marginRight: '5px' }}
              onClick={() => {
                this.setState({
                  selectedYear: this.state.selectedYear - 1,
                });
              }}
            />
          </Tooltip>
          <Label
            style={{
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '25px',
              verticalAlign: 'center',
            }}
          >
            {this.state.selectedYear}
          </Label>
          <Tooltip
            content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_NextYear)}
            position={Position.BOTTOM}
            hoverOpenDelay={750}
          >
            <Button
              icon={IconNames.CHEVRON_RIGHT}
              large={true}
              style={{ height: '30px', marginLeft: '5px' }}
              onClick={() => {
                this.setState({
                  selectedYear: this.state.selectedYear + 1,
                });
              }}
            />
          </Tooltip>
        </ContainerSearchForm>
        <Container>
          <DivLine>
            <DivColumn>
              <DataGrid id="BHDList" dataSource={this.buildGridData()} showBorders allowColumnResizing width={'100%'}>
                <Column dataType="string" dataField="bkname" />
                <Column dataType="string" dataField="bkcode" />
                <Column
                  dataType="string"
                  dataField="date"
                  defaultSortOrder="asc"
                  sortIndex={1}
                  calculateCellValue={(data) => moment(new Date(data.date)).format('ddd DD/MM/YYYY')}
                  calculateSortValue={(data) => new Date(data.date)}
                  //format="EEE dd/MM/yyyy"
                />
                <Column caption="Actions" type="buttons" visible={this.state.canEdit}>
                  <DGButton
                    hint={translate(TLCode.GENERAL_Edit)}
                    icon={'edit'}
                    disabled={!this.state.canEdit}
                    onClick={(e) => {
                      this.setState({
                        bankHolidayDateEdition: {
                          ...e.row!.data,
                          date: moment(new Date(e.row!.data.date)).format('DD/MM/YYYY'),
                        },
                      });
                    }}
                  />
                  <DGButton
                    hint={translate(TLCode.GENERAL_Delete)}
                    icon={'clear'}
                    disabled={!this.state.canEdit}
                    onClick={(e) => {
                      this.setState({ confirmDeleteOpen: true, selectedItemIdToDelete: e.row!.data.id });
                    }}
                  />
                </Column>
              </DataGrid>
            </DivColumn>
          </DivLine>
        </Container>
      </Container>
    );
  }
}

export default withTranslate(BankHolidayDateManager);
